import { LegacyApiError, legacyApiRequest, ValidationError, RateLimitError } from "./legacy-api-request";
import type { LegacyForgotPasswordResponse, LegacyForgotPasswordErrorResponse } from "./legacy-types";

/**
 * Reset Password API
 */
export const legacyResetPasswordApi = {
  /**
   * Request a password reset.
   *
   * @param email - The user's email address
   * @throws {ValidationError} - When email is invalid (400 response)
   * @throws {RateLimitError} - When rate limited (429 response)
   * @throws {NetworkError} - When network request fails
   * @throws {LegacyApiError} - For other API errors
   */
  async send(email: string): Promise<void> {
    try {
      await legacyApiRequest<LegacyForgotPasswordResponse>({
        method: "POST",
        path: "/forgotten/",
        body: { email },
        logMessage: "Requesting password reset:",
      });
    } catch (error) {
      if (error instanceof LegacyApiError) {
        // Parse error response
        let errorData: LegacyForgotPasswordErrorResponse = {};
        try {
          errorData = JSON.parse(error.body ?? "{}");
        } catch {
          // Ignore JSON parse errors
        }

        if (error.status === 400 && errorData.email) {
          throw new ValidationError("Please enter your email address.", "email");
        }

        if (error.status === 429) {
          throw new RateLimitError(errorData.detail);
        }

        // Generic API error
        throw new LegacyApiError(error.status, error.statusText, errorData.detail ?? "Unable to reset password.");
      }

      // Re-throw other errors (NetworkError, etc.)
      throw error;
    }
  },
};
